import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SaleItemBulkUpdateService } from '../../sale-item-bulk-update.service';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import moment from 'moment';

export interface SaleItemBulkUpdateImportDialogData {
  update_schedule_at: string;
  minDate: Date;
}

@Component({
  selector: 'app-sale-item-bulk-update-import-dialog',
  templateUrl: './sale-item-bulk-update-import-dialog.component.html',
  styleUrls: ['./sale-item-bulk-update-import-dialog.component.scss'],
  standalone: false
})
export class SaleItemBulkUpdateImportDialogComponent implements OnInit {
  update_schedule_at = '';
  minDate!: Date;

  importFile: File | null = null;
  importDateVal: moment.Moment | null = null;
  importFileError = '';
  importDateError = '';
  isUploading = false;
  isDragOver = false;

  constructor(
    public dialogRef: MatDialogRef<SaleItemBulkUpdateImportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SaleItemBulkUpdateImportDialogData,
    private service: SaleItemBulkUpdateService,
    private snackBarService: SnackBarService
  ) {
    if (data) {
      this.update_schedule_at = data.update_schedule_at;
      this.minDate = data.minDate;
    }
  }

  ngOnInit(): void {}

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.importFile = files[0];
      this.importFileError = '';
    } else {
      this.importFile = null;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension === 'xlsx' || extension === 'xls') {
        this.importFile = file;
        this.importFileError = '';
      } else {
        this.importFile = null;
        this.importFileError = 'Only Excel files (.xlsx, .xls) are accepted.';
      }
    }
  }

  removeFile(event: MouseEvent): void {
    event.stopPropagation();
    this.importFile = null;
    this.importFileError = '';
  }

  confirmImport(): void {
    this.importFileError = '';
    this.importDateError = '';

    if (!this.importFile) {
      this.importFileError = 'Please select a spreadsheet file.';
      return;
    }

    if (!this.importDateVal) {
      this.importDateError = 'Please select a schedule publication date.';
      return;
    }

    const formattedDate = this.importDateVal.format('YYYY-MM-DD');
    this.isUploading = true;

    this.service.importTemplate(this.importFile, formattedDate).subscribe({
      next: (response) => {
        this.isUploading = false;
        if (response.status === 'SUCCESS') {
          this.showSuccess('Spreadsheet imported successfully.');
          this.dialogRef.close(true);
        } else {
          this.showError(response.message || 'Import parsing failed.');
        }
      },
      error: (error) => {
        this.isUploading = false;
        this.showError(error?.error?.message || 'Network error uploading import sheet.');
      }
    });
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  private showSuccess(msg: string): void {
    const data: SnackBarData = {
      message: msg,
      title: 'Success',
      type: 'success'
    };
    this.snackBarService.openSnackBar(data);
  }

  private showError(msg: string): void {
    const data: SnackBarData = {
      message: msg,
      title: 'Failed',
      type: 'error'
    };
    this.snackBarService.openSnackBar(data);
  }
}
